/**
 * 该文件可自行根据业务逻辑进行调整
 */
import type {
  HttpResponse,
  RequestClientOptions,
  RequestResponse,
} from '@vben/request';

import type { WebPerformanceLogForm } from './modules/web-logs';

import { preferences } from '@vben/preferences';
import {
  defaultResponseInterceptor,
  errorMessageResponseInterceptor,
  RequestClient,
} from '@vben/request';
import { useAccessStore, useUserStore } from '@vben/stores';

import { message, notification } from 'ant-design-vue';

import { getRuntimeApiBaseUrl } from '#/api/runtime-config';
import { updateTokenCreatedAt } from '#/composables/useSessionTimeout';
import { $t } from '#/locales';
import { useAuthStore } from '#/store';
import { getDeviceId, getSessionId } from '#/utils/device';
import { reportApiError } from '#/utils/error-handler';
import { normalizeHttpError } from '#/utils/http-error';
import {
  clearPersistedRefreshToken,
  readPersistedAccessToken,
  readPersistedRefreshToken,
  savePersistedRefreshToken,
} from '#/utils/token-sync';

import {
  createTauriHttpAdapter,
  shouldUseTauriHttpAdapter,
} from './adapter/tauri-http';
import { webLogsApi } from './modules/web-logs';

// API 性能监控配置
interface ApiPerformanceConfig {
  /** 是否启用 API 性能监控 */
  enabled: boolean;
  /** 慢请求阈值（毫秒） */
  slowThreshold: number;
  /** 采样率 (0-1) */
  sampleRate: number;
  /** 是否仅监控慢请求 */
  slowOnly: boolean;
}

const API_PERF_CONFIG: ApiPerformanceConfig = {
  enabled: import.meta.env.PROD, // 生产环境启用
  slowThreshold: 2000, // 2秒算慢请求
  sampleRate: 0.2, // 20% 采样率
  slowOnly: false, // 监控所有请求
};

export interface RefreshTokenPayload {
  expiresIn: number;
  token: string;
  tokenType: string;
}

/** 独立 Refresh Token 轮换响应（POST /auth/refresh-token） */
export interface RefreshAccessTokenPayload {
  expiresIn: number;
  refreshExpiresIn: number;
  refreshToken: string;
  token: string;
  tokenType: string;
}

export type RawRefreshTokenResponse = RequestResponse<
  HttpResponse<RefreshTokenPayload>
>;

interface CurrentUserInfo {
  displayName: null | string;
  realName: null | string;
  userId: null | number;
  username: null | string;
}

interface NormalizedApiUrl {
  endpoint: string;
  fullUrl: string;
  path: string;
  queryKeys: string[];
  safeFullUrl: string;
}

// 存储请求开始时间
const requestTimings = new Map<string, number>();

/**
 * 生成请求唯一标识
 */
function generateRequestId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function formatLocalDateTime(date: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function truncateText(value: string, maxLength: number): string {
  return value.length > maxLength ? value.slice(0, maxLength) : value;
}

function safePageUrl(): string {
  return `${window.location.origin}${window.location.pathname}`;
}

/**
 * 获取当前用户信息
 */
function getCurrentUserInfo(): CurrentUserInfo {
  try {
    const userStore = useUserStore();
    const userInfo = userStore.userInfo as any;
    const username = userInfo?.username || null;
    const realName =
      userInfo?.realName || userInfo?.name || userInfo?.nickname || null;

    return {
      userId: userInfo?.id || null,
      username,
      realName,
      displayName: realName || username,
    };
  } catch {
    return {
      userId: null,
      username: null,
      realName: null,
      displayName: null,
    };
  }
}

/**
 * 需要排除 API 性能监控的 URL 模式（避免无限循环）
 *
 * 说明：这些是日志上报接口，不能监控自身，否则会无限循环
 * - /api/v1/web-logs/：Web 前端日志上报
 * - /api/v1/app-logs/：移动 App 日志上报
 */
const EXCLUDED_URL_PATTERNS = [
  '/api/v1/web-logs/errors',
  '/api/v1/web-logs/performance',
  '/api/v1/web-logs/behavior',
  '/api/v1/app-logs/',
];

/**
 * 检查 URL 是否应该被排除监控
 */
function shouldExcludeUrl(url: string): boolean {
  const normalized = normalizeApiUrl(url);
  return EXCLUDED_URL_PATTERNS.some(
    (pattern) =>
      normalized.path.includes(pattern) || normalized.fullUrl.includes(pattern),
  );
}

function shouldSkipAuthError(error: any): boolean {
  const requestUrl = String(error?.config?.url ?? '');
  return requestUrl.includes('/api/v1/auth/login');
}

let performanceLogCooldownUntil = 0;

function isPerformanceLogCoolingDown(): boolean {
  return Date.now() < performanceLogCooldownUntil;
}

function markPerformanceLogLimited(error: any): void {
  if (error?.response?.status !== 429) {
    return;
  }

  const retryAfter = Number(error?.response?.headers?.['retry-after']);
  performanceLogCooldownUntil =
    Date.now() +
    (Number.isFinite(retryAfter) && retryAfter > 0
      ? retryAfter * 1000
      : 60_000);
}

function normalizeApiUrl(url: string): NormalizedApiUrl {
  const fallbackPath = String(url || '').split('?')[0] || '';
  const baseUrl = finalApiURL || window.location.origin;

  try {
    const parsed = new URL(url || '/', baseUrl);
    const queryKeys = [...new Set(parsed.searchParams.keys())];

    return {
      endpoint: parsed.pathname,
      fullUrl: parsed.toString(),
      path: parsed.pathname,
      queryKeys,
      safeFullUrl: `${parsed.origin}${parsed.pathname}`,
    };
  } catch {
    return {
      endpoint: fallbackPath,
      fullUrl: String(url || ''),
      path: fallbackPath,
      queryKeys: [],
      safeFullUrl: fallbackPath,
    };
  }
}

/**
 * API 性能日志缓冲队列
 *
 * 说明：接口耗时采集对全量 API 请求按采样率生效，若每条单独 POST 会产生
 * 接近采样率比例的额外上报请求。因此先入缓冲，满批 / 定时 / 页面隐藏时批量刷出。
 */
const API_PERF_BATCH_SIZE = 20;
const API_PERF_FLUSH_INTERVAL = 30_000;
const API_PERF_BUFFER_MAX = 200;
const apiPerfBuffer: WebPerformanceLogForm[] = [];
let apiPerfFlushTimer: null | ReturnType<typeof setInterval> = null;

async function buildApiPerfLog(
  url: string,
  method: string,
  duration: number,
  statusCode: number,
  success: boolean,
  requestId?: string,
): Promise<WebPerformanceLogForm> {
  const deviceId = await getDeviceId();
  const sessionId = getSessionId();
  const userInfo = getCurrentUserInfo();
  const normalizedUrl = normalizeApiUrl(url);
  const endpointMethod = method.toUpperCase();
  const endpoint = `${endpointMethod} ${normalizedUrl.path}`;

  return {
    device_id: deviceId,
    session_id: sessionId,
    user_id: userInfo.userId,
    metric_type: 'api_request',
    metric_name: truncateText(endpoint, 100),
    metric_value: duration,
    metric_unit: 'ms',
    metric_category: success ? 'success' : 'error',
    page_url: safePageUrl(),
    page_route: window.location.pathname,
    is_slow: duration > API_PERF_CONFIG.slowThreshold,
    threshold: API_PERF_CONFIG.slowThreshold,
    context: {
      method: endpointMethod,
      path: normalizedUrl.path,
      endpoint,
      endpointMethod,
      endpointPath: normalizedUrl.path,
      fullUrl: normalizedUrl.safeFullUrl,
      queryKeys: normalizedUrl.queryKeys,
      pageTitle: document.title || null,
      pageRoute: window.location.pathname,
      requestId: requestId || null,
      statusCode,
      success,
      user: {
        username: userInfo.username,
        realName: userInfo.realName,
        displayName: userInfo.displayName,
      },
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    occurred_at: formatLocalDateTime(new Date()),
  };
}

/** 批量刷出缓冲中的 API 性能日志；失败时静默处理并按 429 冷却 */
async function flushApiPerfBuffer(): Promise<void> {
  if (apiPerfBuffer.length === 0 || isPerformanceLogCoolingDown()) return;

  const logs = apiPerfBuffer.splice(0, API_PERF_BATCH_SIZE);
  try {
    await webLogsApi.batchUploadPerformance({ logs });
  } catch (error: any) {
    markPerformanceLogLimited(error);
    // 认证失效：清空剩余缓冲，避免登录过期期间持续产生失败请求
    const isAuthError =
      error?.response?.status === 401 ||
      String(error?.message || '').includes('Unauthenticated');
    if (isAuthError) {
      apiPerfBuffer.length = 0;
    }
    // 其他错误：本批日志丢弃不回灌，避免失败请求无限膨胀
  }
}

function ensureApiPerfFlushTimer(): void {
  if (apiPerfFlushTimer) return;
  apiPerfFlushTimer = setInterval(flushApiPerfBuffer, API_PERF_FLUSH_INTERVAL);
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flushApiPerfBuffer();
    }
  });
}

/**
 * 上报 API 性能数据（入缓冲队列，满批 / 定时 / 页面隐藏时批量刷出）
 */
async function reportApiPerformance(
  url: string,
  method: string,
  duration: number,
  statusCode: number,
  success: boolean,
  requestId?: string,
): Promise<void> {
  // 检查是否启用
  if (!API_PERF_CONFIG.enabled) return;

  // 排除监控接口本身，避免无限循环！
  if (shouldExcludeUrl(url)) return;

  // 检查是否慢请求
  const isSlow = duration > API_PERF_CONFIG.slowThreshold;

  // 仅监控慢请求模式
  if (API_PERF_CONFIG.slowOnly && !isSlow) return;

  // 采样判断
  if (Math.random() > API_PERF_CONFIG.sampleRate && !isSlow) return;

  // 缓冲硬上限：防止异常情况下内存无限增长
  if (apiPerfBuffer.length >= API_PERF_BUFFER_MAX) return;

  try {
    const log = await buildApiPerfLog(
      url,
      method,
      duration,
      statusCode,
      success,
      requestId,
    );
    apiPerfBuffer.push(log);
    ensureApiPerfFlushTimer();
    if (apiPerfBuffer.length >= API_PERF_BATCH_SIZE) {
      flushApiPerfBuffer();
    }
  } catch {
    // 构建日志失败不影响业务
  }
}

const finalApiURL = getRuntimeApiBaseUrl();

/**
 * 请求超时配置（毫秒）
 * 根据不同操作类型设置合理的超时时间
 */
export const REQUEST_TIMEOUT = {
  /** 默认超时时间：15秒 */
  DEFAULT: 15_000,
  /** 列表查询：20秒（可能有大量数据） */
  LIST: 20_000,
  /** 详情查询：10秒 */
  DETAIL: 10_000,
  /** 创建/更新操作：30秒 */
  MUTATION: 30_000,
  /** 文件上传：2分钟 */
  UPLOAD: 120_000,
  /** 文件下载：3分钟 */
  DOWNLOAD: 180_000,
  /** 报表/导出：1分钟 */
  REPORT: 60_000,
  /** 批量操作：2分钟 */
  BATCH: 120_000,
  /** 文件迁移客户端等待上限：10分钟；网关可能更早超时。 */
  MIGRATION: 600_000,
  /** 钉钉组织同步：全量读取部门和成员，最长等待 15 分钟。 */
  DINGTALK_SYNC: 900_000,
} as const;

/** 最近一次认证失败的后端原因（用于向用户透传被登出的真实原因） */
let lastAuthFailReason = '';

/**
 * 会话失效标志：doReAuthenticate 触发后置位，重新登录成功后复位。
 *
 * 置位期间所有 401 直接抛错、不再发起刷新请求——否则登录过期弹窗挂在页面上时，
 * 各轮询组件的每个请求都会附带一次注定失败的 /auth/refresh-token 调用，
 * 在控制台刷屏成倍的 401 资源错误。
 */
let sessionInvalidated = false;

/** 重新登录成功后复位失效标志（auth store 登录流程调用） */
export function resetSessionInvalidated(): void {
  sessionInvalidated = false;
}

function markSessionInvalidated(): void {
  sessionInvalidated = true;
}

/**
 * 防止重入标记
 */
let isReAuthenticating = false;

/**
 * 重新认证逻辑
 *
 * 说明：
 * - 当Token过期或无效时，清除本地Token并执行注销
 * - 添加防重入保护，避免无限循环
 * - 确保即使logout API失败，也能正确清除状态
 * - 会话超时后强制跳转到登录页
 */
async function doReAuthenticate() {
  // 防止重入：如果正在执行认证流程，直接返回
  if (isReAuthenticating) {
    console.warn('[认证拦截器] 已在执行认证流程，跳过重复调用');
    return;
  }

  isReAuthenticating = true;

  try {
    // 置位失效标志：后续 401 不再重试刷新（重新登录成功后由 authLogin 复位）
    markSessionInvalidated();

    console.warn('会话已超时或Token无效，即将跳转到登录页');
    const accessStore = useAccessStore();
    const authStore = useAuthStore();

    // 同步清除独立 Refresh Token（内存 + 明文持久层），失效凭证不残留
    accessStore.setRefreshToken(null);
    clearPersistedRefreshToken();

    // 透传后端给出的真实原因（如：设备登录状态已失效＝账号在其他设备登录，
    // 而非笼统的"登录已过期"，避免用户误判）
    if (lastAuthFailReason) {
      notification.warning({
        message: $t('app.auth.invalidTitle'),
        description: $t('app.auth.invalidContent', {
          reason: lastAuthFailReason,
        }),
        duration: 6,
      });
      lastAuthFailReason = '';
    }

    // 清除Token
    accessStore.setAccessToken(null);

    // 会话超时后，强制跳转到登录页
    // 使用 modal 模式时显示过期提示，否则直接跳转
    if (
      preferences.app.loginExpiredMode === 'modal' &&
      accessStore.isAccessChecked
    ) {
      accessStore.setLoginExpired(true);
    } else {
      // 跳转到登录页，并保存当前路径用于登录后返回
      await authStore.logout(true);
    }
  } finally {
    // 重置标记，允许下次认证
    // 延迟重置，避免并发请求立即触发
    setTimeout(() => {
      isReAuthenticating = false;
    }, 1000);
  }
}

/**
 * 刷新token逻辑
 *
 * 优先走独立 Refresh Token 通道（POST /auth/refresh-token）：携带长效刷新凭证，
 * access token 已过期后仍可换取新会话——这是锁屏/后台标签被浏览器冻结后的恢复通道。
 * 没有独立凭证时（旧后端、或升级前已建立的会话）回退旧的 access token 轮换端点。
 *
 * ⚠️ 两个通道都使用 baseRequestClient：不经过 401 响应拦截器，
 *    刷新失败直接抛错，由调用方处理（触发重新认证）。
 *
 * ⚠️ 后端两条刷新通道都是一次性轮换（成功即删旧凭证）：
 * - 独立通道：同浏览器多标签页并发时，后到者携带已轮换的旧 refresh token，
 *   后端返回 401 + reason=refresh_rotated_grace（宽限窗内不撤销）；
 *   此时读明文持久层最新凭证重试一次即可收敛。
 * - 旧通道：刷新前/失败后检查 localStorage 是否已有更新的 access token。
 */
async function doRefreshToken() {
  const accessStore = useAccessStore();

  const persistedRefresh = readPersistedRefreshToken();
  const currentRefresh = accessStore.refreshToken || persistedRefresh;

  if (currentRefresh) {
    try {
      return await refreshWithIndependentToken(currentRefresh);
    } catch (error) {
      // 跨标签页竞态：本页凭证已被其它标签页轮换，持久层若有更新的凭证则换值重试一次
      const latestRefresh = readPersistedRefreshToken();
      if (latestRefresh && latestRefresh !== currentRefresh) {
        return await refreshWithIndependentToken(latestRefresh);
      }
      // 登录过期期间（失效标志置位）的刷新请求直接失败，不回退旧通道
      throw error;
    }
  }

  return doRefreshTokenLegacy(accessStore);
}

/**
 * 独立 Refresh Token 通道：POST /auth/refresh-token
 * 响应同时携带新 access token 与轮换后的新 refresh token，双写内存与持久层。
 */
async function refreshWithIndependentToken(refreshToken: string): Promise<string> {
  const accessStore = useAccessStore();

  const headers: Record<string, string> = {};
  try {
    headers['X-Device-ID'] = await getDeviceId();
  } catch {
    // 设备指纹失败时静默：后端会以 device_id_missing 拒绝并走重新认证
  }

  const response = (await baseRequestClient.post<
    HttpResponse<RefreshAccessTokenPayload>
  >(
    '/api/v1/auth/refresh-token',
    { refresh_token: refreshToken },
    { headers },
  )) as unknown as RequestResponse<HttpResponse<RefreshAccessTokenPayload>>;

  if (response.data.code !== 0) {
    lastAuthFailReason = response.data.message || '';
    throw new Error(
      response.data.message || $t('common.httpError.tokenRefreshFailed'),
    );
  }

  const { refreshToken: nextRefreshToken, token: newToken } = response.data.data;
  accessStore.setAccessToken(newToken);
  accessStore.setRefreshToken(nextRefreshToken);
  savePersistedRefreshToken(nextRefreshToken);

  // 同步 Token 创建时间（主动刷新判断的基准）
  updateTokenCreatedAt();

  return newToken;
}

/**
 * 旧通道：携带仍有效的 access token 调 POST /auth/refresh（一次性轮换）。
 * 仅在会话没有独立 Refresh Token 时使用（旧后端兼容 / 存量会话平滑过渡）。
 */
async function doRefreshTokenLegacy(accessStore: ReturnType<typeof useAccessStore>): Promise<string> {
  const attemptRefresh = async (token: null | string): Promise<string> => {
    // 构建请求头（必须手动添加，因为 baseRequestClient 没有请求拦截器）
    const headers: Record<string, string> = {};

    // 添加当前 Token（刷新接口需要认证）
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    // 添加设备 ID
    try {
      const deviceId = await getDeviceId();
      headers['X-Device-ID'] = deviceId;
    } catch {
      // 静默失败
    }

    // 使用 baseRequestClient 发送刷新请求，跳过 401 拦截器
    // 这样刷新请求失败时不会加入队列，直接抛出错误
    const response = (await baseRequestClient.post<
      HttpResponse<RefreshTokenPayload>
    >('/api/v1/auth/refresh', undefined, {
      headers,
    })) as unknown as RawRefreshTokenResponse;

    // 检查响应
    if (response.data.code !== 0) {
      lastAuthFailReason = response.data.message || '';
      throw new Error(
        response.data.message || $t('common.httpError.tokenRefreshFailed'),
      );
    }

    const newToken = response.data.data.token;
    accessStore.setAccessToken(newToken);

    // 方案B：更新 Token 创建时间，用于智能刷新判断
    updateTokenCreatedAt();

    return newToken;
  };

  // 跨标签页：另一标签页可能刚完成轮换（本页 401 的请求携带的是旧 token，
  // 但内存状态尚未收到 storage 同步）。若 localStorage 已有不同 token，
  // 直接采纳复用，不再发起刷新。
  const persistedToken = readPersistedAccessToken();
  if (persistedToken && persistedToken !== accessStore.accessToken) {
    accessStore.setAccessToken(persistedToken);
    updateTokenCreatedAt();
    return persistedToken;
  }

  const usedToken = accessStore.accessToken;
  try {
    return await attemptRefresh(usedToken);
  } catch (error) {
    const latestPersistedToken = readPersistedAccessToken();
    if (!latestPersistedToken || latestPersistedToken === usedToken) {
      throw error;
    }
    accessStore.setAccessToken(latestPersistedToken);
    return await attemptRefresh(latestPersistedToken);
  }
}

function formatToken(token: null | string) {
  return token ? `Bearer ${token}` : null;
}

/**
 * 全局共享的 Token 刷新协调
 *
 * requestClient / uploadClient / downloadClient / reportClient / batchClient
 * 五个实例共用同一把刷新锁与等待队列：后端 /auth/refresh 是一次性轮换
 * （刷新成功即删除旧 token），任何两个刷新并发时，后到者携带的 token 已被
 * 删除而必然 401，表现为"使用中被踢回登录页"。主动刷新（useSessionTimeout）
 * 与被动刷新（401 拦截器）都必须经由本函数串行化。
 */
let isRefreshingToken = false;
let sharedRefreshTokenQueue: Array<(newToken: string) => void> = [];

async function refreshAccessToken(): Promise<string> {
  // 会话已失效（登录过期弹窗已挂出）：不再发起注定失败的刷新请求
  if (sessionInvalidated) {
    throw new Error($t('common.httpError.tokenRefreshFailed'));
  }

  if (isRefreshingToken) {
    return new Promise<string>((resolve, reject) => {
      sharedRefreshTokenQueue.push((newToken: string) => {
        if (newToken) {
          resolve(newToken);
        } else {
          reject(new Error($t('common.httpError.tokenRefreshFailed')));
        }
      });
    });
  }

  isRefreshingToken = true;
  try {
    const newToken = await doRefreshToken();
    sharedRefreshTokenQueue.forEach((callback) => callback(newToken));
    sharedRefreshTokenQueue = [];
    return newToken;
  } catch (error) {
    sharedRefreshTokenQueue.forEach((callback) => callback(''));
    sharedRefreshTokenQueue = [];
    throw error;
  } finally {
    isRefreshingToken = false;
  }
}

/**
 * 401 响应拦截器（共享刷新锁版本）
 *
 * 与框架 authenticateResponseInterceptor 行为一致，但刷新状态收口到模块级
 * refreshAccessToken()：所有客户端实例的并发 401 只触发一次刷新，刷新完成
 * 后各自携带新 token 重放。
 */
function createAuthResponseInterceptor(client: RequestClient) {
  return {
    rejected: async (error: any) => {
      const { config, response } = error;
      // 如果不是 401 错误，直接抛出异常
      if (response?.status !== 401) {
        throw error;
      }
      // 会话已失效（重新登录弹窗已挂出）：直接抛错，不再逐请求重试刷新，
      // 避免轮询组件在弹窗存续期间把控制台刷成 401 资源错误
      if (sessionInvalidated) {
        throw error;
      }
      // 登录等公开接口的 401 是业务失败，不应触发会话过期流程
      if (shouldSkipAuthError(error)) {
        throw error;
      }
      // 未启用刷新或已经是重试请求：直接进入重新认证
      if (!preferences.app.enableRefreshToken || config.__isRetryRequest) {
        await doReAuthenticate();
        throw error;
      }
      // 标记重试，避免重放后再次 401 造成无限刷新
      config.__isRetryRequest = true;
      try {
        const newToken = await refreshAccessToken();
        config.headers.Authorization = formatToken(newToken);
        return client.request(config.url, { ...config });
      } catch (refreshError) {
        console.error('Refresh token failed, please login again.');
        await doReAuthenticate();
        throw refreshError;
      }
    },
  };
}

function createRequestClient(baseURL: string, options?: RequestClientOptions) {
  const client = new RequestClient({
    ...options,
    baseURL,
    ...(shouldUseTauriHttpAdapter()
      ? { adapter: createTauriHttpAdapter() }
      : {}),
  });

  function formatToken(token: null | string) {
    return token ? `Bearer ${token}` : null;
  }

  /**
   * 请求头处理
   *
   * 添加的请求头：
   * - Authorization: Bearer Token
   * - Accept-Language: 用户语言偏好
   * - X-Device-ID: 设备指纹ID（用于Token设备绑定）
   * - X-CSRF-TOKEN: CSRF令牌（防止跨站请求伪造）
   * - X-Request-ID: 请求唯一标识（用于性能监控）
   */
  client.addRequestInterceptor({
    fulfilled: async (config) => {
      const accessStore = useAccessStore();

      // 0. 记录请求开始时间（用于性能监控）
      const requestId = generateRequestId();
      config.headers['X-Request-ID'] = requestId;
      requestTimings.set(requestId, Date.now());

      // 1. 添加Bearer Token
      config.headers.Authorization = formatToken(accessStore.accessToken);

      // 2. 添加语言偏好
      config.headers['Accept-Language'] = preferences.app.locale;

      // 3. 添加设备ID到请求头
      // 说明：后端使用设备ID进行Token绑定验证
      // 如果设备ID不匹配，后端会返回401错误
      try {
        const deviceId = await getDeviceId();
        config.headers['X-Device-ID'] = deviceId;
      } catch (error) {
        // 获取设备ID失败时记录错误，但不阻止请求
        // 后端会使用备用机制生成设备ID
        console.error('无法获取设备ID:', error);
      }

      // 4. 添加CSRF Token（如果页面上有的话）
      // 说明：
      // - CSRF Token用于防止跨站请求伪造攻击
      // - 通常由后端在页面HTML中设置meta标签
      // - 前端在每次请求时带上，后端进行验证
      // - 如果没有CSRF Token，说明后端未启用CSRF防护，不影响请求
      try {
        const csrfToken = document
          .querySelector('meta[name="csrf-token"]')
          ?.getAttribute('content');

        if (csrfToken) {
          config.headers['X-CSRF-TOKEN'] = csrfToken;
        }
      } catch (error) {
        // 获取CSRF Token失败时记录错误，但不阻止请求
        // 如果后端未启用CSRF防护，此错误可以忽略
        console.warn('无法获取CSRF Token:', error);
      }

      return config;
    },
  });

  /**
   * API 性能监控响应拦截器
   * 记录每个请求的耗时并上报
   */
  client.addResponseInterceptor({
    fulfilled: (response) => {
      const requestId = response.config?.headers?.['X-Request-ID'] as string;
      const startTime = requestTimings.get(requestId);

      if (startTime) {
        const duration = Date.now() - startTime;
        requestTimings.delete(requestId);

        // 异步上报，不阻塞响应
        reportApiPerformance(
          response.config?.url || '',
          response.config?.method || 'GET',
          duration,
          response.status,
          true,
          requestId,
        );
      }

      return response;
    },
    rejected: (error) => {
      const requestId = error.config?.headers?.['X-Request-ID'] as string;
      const startTime = requestTimings.get(requestId);

      if (startTime) {
        const duration = Date.now() - startTime;
        requestTimings.delete(requestId);

        // 异步上报，不阻塞响应
        reportApiPerformance(
          error.config?.url || '',
          error.config?.method || 'GET',
          duration,
          error.response?.status || 0,
          false,
          requestId,
        );
      }

      return Promise.reject(error);
    },
  });

  // 处理返回的响应数据格式
  // 注意：后端统一使用 ApiResponse Trait
  // - 成功: {code: 0, message: 'success', data: {...}}
  // - 创建成功: {code: 0, http=201，建议前后端统一以 code=0 判成功，HTTP 决定语义}
  // - 失败: {code: 4xxxx/5xxxx, message: '错误信息', data: null}
  client.addResponseInterceptor(
    defaultResponseInterceptor({
      codeField: 'code',
      dataField: 'data',
      // 仅 code === 0 判定成功，HTTP 201 由后端返回 created 语义
      successCode: (code: number) => code === 0,
    }),
  );

  // token过期的处理
  client.addResponseInterceptor(createAuthResponseInterceptor(client));

  // 通用的错误处理,如果没有进入上面的错误处理逻辑，就会进入这里
  client.addResponseInterceptor(
    errorMessageResponseInterceptor((msg: string, error) => {
      // 静默请求（如全局万能检索的后台探查）：不弹提示、不上报，错误由调用方自行处理
      if (error?.config?.silentError) {
        return;
      }
      // 获取错误详情
      const responseData = error?.response?.data ?? {};
      const statusCode = error?.response?.status;
      const requestUrl = error?.config?.url ?? '';
      const requestMethod = String(
        error?.config?.method || 'GET',
      ).toUpperCase();
      const requestId = error?.config?.headers?.['X-Request-ID'] as
        | string
        | undefined;
      const normalizedUrl = normalizeApiUrl(requestUrl);
      const endpoint = `${requestMethod} ${normalizedUrl.path}`;

      // 非登录接口的 401 已被 401 响应拦截器处理（跳转登录页/刷新Token），
      // 此处不再重复弹出错误提示，避免误导用户；登录接口的 401 仍需提示密码错误等业务信息。
      if (statusCode === 401 && !shouldSkipAuthError(error)) {
        return;
      }

      const userFriendlyMessage = normalizeHttpError(error, {
        fallbackMessage: msg,
      });

      // 显示错误提示
      message.error(userFriendlyMessage);

      // 上报 API 错误（仅对 5xx 错误和网络错误）
      // ⚠️ 排除日志上报接口本身，避免循环上报！
      if (
        (statusCode >= 500 || !statusCode) && // 检查是否是日志上报接口（避免循环）
        !shouldExcludeUrl(requestUrl)
      ) {
        reportApiError(userFriendlyMessage, {
          url: normalizedUrl.path,
          fullUrl: normalizedUrl.safeFullUrl,
          endpoint,
          endpointMethod: requestMethod,
          endpointPath: normalizedUrl.path,
          method: requestMethod,
          path: normalizedUrl.path,
          queryKeys: normalizedUrl.queryKeys,
          requestId: requestId || null,
          statusCode,
          errorCode: error?.errorCode,
          originalMessage: msg,
          responseData,
          validationErrors: error?.validationErrors,
        });
      }
    }),
  );

  return client;
}

/**
 * 标准请求客户端（自动解包响应数据）
 * 默认超时时间：15秒
 */
export const requestClient = createRequestClient(finalApiURL, {
  responseReturn: 'data',
  timeout: REQUEST_TIMEOUT.DEFAULT,
});

/**
 * 基础请求客户端（原始响应）
 */
export const baseRequestClient = new RequestClient({
  baseURL: finalApiURL,
  ...(shouldUseTauriHttpAdapter() ? { adapter: createTauriHttpAdapter() } : {}),
  timeout: REQUEST_TIMEOUT.DEFAULT,
});

/**
 * 上传专用请求客户端
 * 超时时间：2分钟
 */
export const uploadClient = createRequestClient(finalApiURL, {
  responseReturn: 'data',
  timeout: REQUEST_TIMEOUT.UPLOAD,
});

/**
 * 下载专用请求客户端
 * 超时时间：3分钟
 */
export const downloadClient = createRequestClient(finalApiURL, {
  responseReturn: 'raw',
  timeout: REQUEST_TIMEOUT.DOWNLOAD,
});

/**
 * 报表/导出专用请求客户端
 * 超时时间：1分钟
 */
export const reportClient = createRequestClient(finalApiURL, {
  responseReturn: 'data',
  timeout: REQUEST_TIMEOUT.REPORT,
});

/**
 * 批量导入专用请求客户端
 * 超时时间：2分钟（REQUEST_TIMEOUT.BATCH）
 *
 * 用途：数据导入等批量提交。后端导入是长事务操作，
 * 走 15 秒默认超时会前端报错而后端仍在执行，用户重试会造成并发导入堆积。
 */
export const batchClient = createRequestClient(finalApiURL, {
  responseReturn: 'data',
  timeout: REQUEST_TIMEOUT.BATCH,
});

/**
 * 共享 Token 刷新入口
 *
 * 主动刷新（useSessionTimeout 的定时器/活动事件）必须走这里，
 * 与五个请求客户端 401 拦截器的被动刷新共用同一把锁与等待队列，
 * 避免并发刷新触发后端一次性轮换的 token 被删 → 401 → 被踢回登录页。
 */
export { refreshAccessToken };

/**
 * 静默请求客户端（日志上报专用）
 *
 * 特点：
 * - 不显示错误提示（静默失败）
 * - 不触发错误上报（避免循环）
 * - 仅在控制台输出错误（开发环境）
 *
 * 用途：
 * - 前端错误日志上报
 * - 前端性能日志上报
 */
function createSilentClient(baseURL: string, options?: RequestClientOptions) {
  const client = new RequestClient({
    ...options,
    baseURL,
    ...(shouldUseTauriHttpAdapter()
      ? { adapter: createTauriHttpAdapter() }
      : {}),
  });

  // 只添加基本的请求头处理（Token、设备ID等）
  client.addRequestInterceptor({
    fulfilled: async (config) => {
      const accessStore = useAccessStore();

      // 添加Bearer Token（可选，日志接口可能不需要鉴权）
      if (accessStore.accessToken) {
        config.headers.Authorization = `Bearer ${accessStore.accessToken}`;
      }

      // 添加设备ID
      try {
        const deviceId = await getDeviceId();
        config.headers['X-Device-ID'] = deviceId;
      } catch {
        // 静默失败
      }

      return config;
    },
  });

  // 添加响应数据解包（与标准客户端一致）
  client.addResponseInterceptor(
    defaultResponseInterceptor({
      codeField: 'code',
      dataField: 'data',
      successCode: (code: number) => code === 0,
    }),
  );

  // ⚠️ 注意：不添加 errorMessageResponseInterceptor
  // 日志上报失败时只在控制台输出，不提示用户
  client.addResponseInterceptor({
    fulfilled: (response) => response,
    rejected: (error) => {
      normalizeHttpError(error);

      // 仅开发环境输出调试信息
      if (import.meta.env.DEV) {
        console.warn(
          '[静默请求] 请求失败:',
          error?.config?.url,
          error?.friendlyMessage || error?.message,
        );
      }
      return Promise.reject(error);
    },
  });

  return client;
}

/**
 * 日志上报专用静默请求客户端
 *
 * 用途：前端日志（错误/性能）上报
 * 特点：失败时不提示用户，仅控制台输出
 */
export const silentClient = createSilentClient(finalApiURL, {
  responseReturn: 'data',
  timeout: REQUEST_TIMEOUT.DEFAULT,
});
